import React, {useState, useMemo, useRef, useEffect} from "react";
import {
  Container,
  Card,
  ListGroup,
  Form,
  InputGroup,
  Badge,
  Button,
  Image,
  Dropdown,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaWhatsapp,
  FaPoll,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBuilding,
  FaComment,
  FaPaperPlane,
  FaSearch,
  FaFilter,
} from "react-icons/fa";
import {useLocation} from "react-router-dom";

// Importamos los datos
import pollResponsesData from "../data/pollResponses.json";
import messageResponsesData from "../data/messageResponses.json";
import campaignsData from "../data/campaigns.json";
import contactsData from "../data/contacts.json";

const InboxWhatsApp = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, polls, messages, unread
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  // Función para obtener información de la campaña
  const getCampaignInfo = (campaignId) => {
    return (
      campaignsData.find((c) => c.id === campaignId) || {
        name: "Campaña Desconocida",
      }
    );
  };
  // Función para obtener información del contacto
  const getContactInfo = (contactId) => {
    return (
      contactsData.find((c) => c.id === contactId) || {
        firstName: "Desconocido",
        lastName: "",
        transportista: "",
        phone: "",
      }
    );
  };

  // Combinar y procesar todas las respuestas como conversaciones
  const initialConversations = useMemo(() => {
    // Respuestas de encuestas
    const pollResponses = pollResponsesData.map((response) => ({
      ...response,
      type: "poll",
      contact: getContactInfo(response.contactId),
      campaign: getCampaignInfo(response.campaignId),
      status: "read", // Asumimos que las respuestas de encuestas se marcan como leídas
      priority: "normal",
      lastMessage: `Respondió: "${response.selectedOption}"`,
      sender: "contact",
    }));

    // Respuestas de mensajes
    const messageResponses = messageResponsesData.map((response) => ({
      ...response,
      type: "message",
      contact: getContactInfo(response.contactId),
      campaign: getCampaignInfo(response.campaignId),
      lastMessage: response.responseMessage,
      sender: "contact",
    }));

    // Combinar y agrupar por contacto
    const allResponses = [...pollResponses, ...messageResponses];
    const conversationMap = new Map();

    allResponses.forEach((response) => {
      const key = response.contactId;
      if (!conversationMap.has(key)) {
        conversationMap.set(key, {
          id: key,
          contact: response.contact,
          messages: [],
          lastActivity: response.responseDate,
          unreadCount: response.status === "unread" ? 1 : 0,
          hasHighPriority: response.priority === "high",
        });
      }

      const conversation = conversationMap.get(key);
      conversation.messages.push(response);

      // Actualizar última actividad
      if (
        new Date(response.responseDate) > new Date(conversation.lastActivity)
      ) {
        conversation.lastActivity = response.responseDate;
      }

      // Contar no leídos
      if (response.status === "unread") {
        conversation.unreadCount++;
      }

      // Marcar alta prioridad
      if (response.priority === "high") {
        conversation.hasHighPriority = true;
      }
    });

    return Array.from(conversationMap.values()).sort(
      (a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)
    );
  }, []); // Dependencias vacías para que se calcule una sola vez

  const [conversations, setConversations] = useState(initialConversations);

  const [selectedConversation, setSelectedConversation] = useState(
    conversations[0] || null
  );

  // Efecto para seleccionar la conversación desde el estado de la navegación
  useEffect(() => {
    const contactIdFromState = location.state?.contactId;
    if (contactIdFromState) {
      setConversations((prevConversations) => {
        const convoToSelect = prevConversations.find(
          (c) => c.id === contactIdFromState
        );
        if (convoToSelect) {
          setSelectedConversation(convoToSelect);
          // Mover la conversación seleccionada al principio de la lista
          return [
            convoToSelect,
            ...prevConversations.filter((c) => c.id !== contactIdFromState),
          ];
        }
        return prevConversations; // No se encontró, devolver el estado anterior
      });
    }
    // Limpiar el estado de la navegación para no re-seleccionar en cada renderización
    window.history.replaceState({}, document.title);
  }, [location.state]); // Se ejecuta solo cuando cambia el estado de la navegación

  // Efecto para marcar la conversación como leída al seleccionarla
  useEffect(() => {
    if (selectedConversation) {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConversation.id ? {...c, unreadCount: 0} : c
        )
      );
      messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    }
  }, [selectedConversation]);

  // Efecto para hacer scroll hacia abajo al enviar un mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  }, [selectedConversation]);

  // Filtrar conversaciones
  const filteredConversations = useMemo(() => {
    let filtered = conversations;

    // Filtro por tipo
    if (filterType === "polls") {
      filtered = filtered.filter((conv) =>
        conv.messages.some((r) => r.type === "poll")
      );
    } else if (filterType === "messages") {
      filtered = filtered.filter((conv) =>
        conv.messages.some((r) => r.type === "message")
      );
    } else if (filterType === "unread") {
      filtered = filtered.filter((conv) => conv.unreadCount > 0);
    }

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (conv) =>
          `${conv.contact.firstName} ${conv.contact.lastName}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          conv.contact.transportista
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          conv.messages.some(
            (r) =>
              (r.responseMessage &&
                r.responseMessage
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) ||
              (r.selectedOption &&
                r.selectedOption
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()))
          )
      );
    }

    return filtered;
  }, [conversations, filterType, searchTerm]);

  // Obtener el último mensaje de una conversación
  const getLastMessage = (conversation) => {
    if (conversation.messages.length === 0) return "No hay mensajes";
    const lastResponse = conversation.messages.sort(
      (a, b) => new Date(b.responseDate) - new Date(a.responseDate)
    )[0];

    if (lastResponse.type === "poll") {
      return `📊 Respondió: "${lastResponse.selectedOption}"`;
    } else if (lastResponse.sender === "me") {
      return `Tú: ${lastResponse.responseMessage || lastResponse.text}`;
    } else {
      return lastResponse.responseMessage || lastResponse.text;
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const messageToSend = {
      id: Date.now(),
      text: newMessage,
      sender: "me",
      responseDate: new Date().toISOString(),
      type: "message",
    };

    const updatedConversations = conversations.map((convo) => {
      if (convo.id === selectedConversation.id) {
        const updatedMessages = [...convo.messages, messageToSend];
        return {
          ...convo,
          messages: updatedMessages,
          lastActivity: messageToSend.responseDate,
        };
      }
      return convo;
    });

    setConversations(updatedConversations);
    setSelectedConversation((prev) => ({
      ...prev,
      messages: [...prev.messages, messageToSend],
    }));
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
    setNewMessage("");
  };

  return (
    <Container fluid className="p-4 h-100">
      <Card
        className="shadow-sm rounded-4 border-0 d-flex flex-row"
        style={{height: "calc(100vh - 200px)", overflow: "hidden"}}
      >
        {/* Panel Izquierdo - Lista de Conversaciones */}
        <div
          className="border-end bg-white"
          style={{
            width: "400px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Header con búsqueda y filtros */}
          <div className="p-3 border-bottom bg-light">
            <h5 className="mb-3 fw-bold text-success">
              <FaWhatsapp className="me-2" />
              Conversaciones
            </h5>

            {/* Barra de búsqueda */}
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Buscar contactos, empresas o mensajes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </Button>
              )}
            </InputGroup>

            {/* Filtros */}
            <Row className="g-2">
              <Col>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                  >
                    <FaFilter className="me-1" />
                    {filterType === "all" && "Todos"}
                    {filterType === "unread" && "No leídos"}
                    {filterType === "polls" && "Encuestas"}
                    {filterType === "messages" && "Mensajes"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      active={filterType === "all"}
                      onClick={() => setFilterType("all")}
                    >
                      Todos ({conversations.length})
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={filterType === "unread"}
                      onClick={() => setFilterType("unread")}
                    >
                      No leídos (
                      {conversations.filter((c) => c.unreadCount > 0).length})
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={filterType === "polls"}
                      onClick={() => setFilterType("polls")}
                    >
                      <FaPoll className="me-1" />
                      Encuestas (
                      {
                        conversations.filter((c) =>
                          c.messages.some((m) => m.type === "poll")
                        ).length
                      }
                      )
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={filterType === "messages"}
                      onClick={() => setFilterType("messages")}
                    >
                      <FaWhatsapp className="me-1" />
                      Mensajes (
                      {
                        conversations.filter((c) =>
                          c.messages.some((m) => m.type === "message")
                        ).length
                      }
                      )
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            {/* Contador de resultados */}
            {(searchTerm || filterType !== "all") && (
              <div className="mt-2 text-muted small">
                {filteredConversations.length} de {conversations.length}{" "}
                conversaciones
                {searchTerm && ` que coinciden con "${searchTerm}"`}
              </div>
            )}
          </div>

          {/* Lista de Conversaciones */}
          <div className="flex-grow-1" style={{overflowY: "auto"}}>
            {filteredConversations.length === 0 ? (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                <div className="text-center p-4">
                  <FaSearch className="fs-1 mb-3" />
                  <h6>No se encontraron conversaciones</h6>
                  <p className="small mb-0">
                    {searchTerm
                      ? `No hay resultados para "${searchTerm}"`
                      : "No hay conversaciones que coincidan con el filtro seleccionado"}
                  </p>
                  {(searchTerm || filterType !== "all") && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setSearchTerm("");
                        setFilterType("all");
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <ListGroup
                variant="flush"
                className="conversation-list"
                style={{paddingRight: "5px"}}
              >
                {filteredConversations.map((conversation) => (
                  <ListGroup.Item
                    key={conversation.id}
                    action
                    onClick={() => setSelectedConversation(conversation)}
                    className={`py-3 ${
                      selectedConversation?.id === conversation.id
                        ? "bg-light"
                        : ""
                    }`}
                  >
                    <div className="d-flex align-items-start">
                      {/* Avatar */}
                      <div className="me-3">
                        <Image
                          src="/assets/user.png"
                          roundedCircle
                          width="50"
                          height="50"
                        />
                        {conversation.unreadCount > 0 && (
                          <Badge
                            bg="danger"
                            pill
                            className="position-absolute translate-middle"
                            style={{top: "10px", left: "45px"}}
                          >
                            {conversation.unreadCount}
                          </Badge>
                        )}
                      </div>

                      {/* Contenido */}
                      <div className="flex-grow-1 min-width-0">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <h6 className="mb-0 fw-bold text-truncate me-2">
                            {conversation.contact.firstName}{" "}
                            {conversation.contact.lastName}
                            {conversation.hasHighPriority && (
                              <FaExclamationTriangle className="ms-2 text-danger" />
                            )}
                          </h6>
                          <small className="text-muted">
                            {new Date(
                              conversation.lastActivity
                            ).toLocaleDateString("es-CL")}
                          </small>
                        </div>

                        <div className="text-muted small mb-1">
                          <FaBuilding className="me-1" />
                          {conversation.contact.transportista || "Sin empresa"}
                        </div>

                        <div className="text-truncate small">
                          {getLastMessage(conversation)}
                        </div>

                        <div className="d-flex gap-1 mt-2 flex-wrap">
                          {conversation.messages.some(
                            (r) => r.type === "poll"
                          ) && (
                            <Badge bg="info" className="small">
                              <FaPoll className="me-1" />
                              Respondió Encuesta
                            </Badge>
                          )}
                          {conversation.messages.some(
                            (r) => r.type === "message" && r.sender !== "me"
                          ) && (
                            <Badge bg="success" className="small">
                              <FaWhatsapp className="me-1" />
                              Mensaje
                            </Badge>
                          )}
                          <Badge bg="secondary" className="small">
                            {conversation.messages.length} mensaje
                            {conversation.messages.length > 1 ? "s" : ""}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </div>

        {/* Panel Derecho - Detalle de Conversación */}
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{minWidth: 0, height: "100%"}}
        >
          {selectedConversation ? (
            <>
              {/* Header de la conversación */}
              <div className="p-3 border-bottom bg-light">
                <div className="d-flex align-items-center">
                  <Image
                    src="/assets/user.png"
                    roundedCircle
                    width="40"
                    height="40"
                    className="me-3"
                  />
                  <div>
                    <h6 className="mb-0 fw-bold">
                      {selectedConversation.contact.firstName}{" "}
                      {selectedConversation.contact.lastName}
                    </h6>
                    <small className="text-muted">
                      {selectedConversation.contact.transportista ||
                        "Sin empresa"}{" "}
                      • {selectedConversation.contact.phone}
                    </small>
                  </div>
                </div>
              </div>

              {/* Área de mensajes */}
              <div
                className="flex-grow-1 p-3 d-flex flex-column"
                style={{
                  overflowY: "auto",
                  backgroundColor: "#E5DDD5",
                  backgroundImage: "url(/assets/whatsapp-bg.png)",
                }}
              >
                {selectedConversation.messages
                  .sort(
                    (a, b) =>
                      new Date(a.responseDate) - new Date(b.responseDate)
                  )
                  .map((response, index) => (
                    <div
                      key={`${response.type}-${response.id}-${index}`}
                      className={`d-flex mb-2 ${
                        response.sender === "me"
                          ? "justify-content-end"
                          : "justify-content-start"
                      }`}
                    >
                      <div
                        className={`rounded-3 p-2 shadow-sm position-relative ${
                          response.sender === "me"
                            ? "bg-success-subtle"
                            : "bg-white"
                        }`}
                        style={{maxWidth: "70%"}}
                      >
                        {response.type === "poll" ? (
                          <div>
                            <div className="fw-bold mb-2 small text-muted">
                              <FaPoll className="me-1" />
                              Respuesta de Encuesta
                            </div>
                            <div className="mb-1 small">
                              <strong>P:</strong> {response.pollQuestion}
                            </div>
                            <div className="bg-light rounded p-2 small">
                              <strong>R:</strong> {response.selectedOption}
                            </div>
                          </div>
                        ) : (
                          <div>{response.responseMessage || response.text}</div>
                        )}

                        <div
                          className="text-end mt-1"
                          style={{fontSize: "0.7rem", color: "#667781"}}
                        >
                          {new Date(response.responseDate).toLocaleTimeString(
                            "es-CL",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                          {response.sender === "me" && (
                            <FaCheckCircle className="ms-1" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Área de respuesta */}
              <div className="p-2 border-top bg-light">
                <Form onSubmit={handleSendMessage}>
                  <InputGroup>
                    <Form.Control
                      placeholder="Escribe un mensaje..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    />
                    <Button
                      variant="success"
                      type="submit"
                      disabled={!newMessage.trim()}
                    >
                      <FaPaperPlane />
                    </Button>
                  </InputGroup>
                </Form>
              </div>
            </>
          ) : (
            /* Estado vacío */
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <div className="text-center">
                <FaComment className="fs-1 mb-3" />
                <h5>Selecciona una conversación</h5>
                <p>Elige una conversación de la lista para ver los detalles</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </Container>
  );
};

export default InboxWhatsApp;
